import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Typography
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import insightsApi from "../../api/insightsApi";
import useStore from "../../store";

const HomePage = ({ locale, setLocale }) => {
    const { enqueueSnackbar } = useSnackbar();
    const activeUser = useStore((state) => state.activeUser);
    const { backgroundColor } = useOutletContext();
    const [recentReports, setRecentReports] = useState([]);
    const [reportsLoading, setReportsLoading] = useState(true);
    const [totalReports, setTotalReports] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        async function fetchData() {
            try {
                setReportsLoading(true);
                const insightsResponse = await insightsApi.getInsightsList({ page: 1, page_size: 6 });
                setTotalReports(insightsResponse.count || 0);

                const mappedReports = insightsResponse.results?.map(insight => ({
                    id: insight.id,
                    surveyId: insight.survey_id,
                    insightId: insight.insight_id,
                    surveyName: insight.survey_title,
                    totalResponses: insight.survey_response_count,
                    lastUpdated: insight.survey_created,
                    status: "completed"
                })) || [];

                setRecentReports(mappedReports);
            } catch (error) {
                console.error("Error fetching insights:", error);
                setTotalReports(0);
                setRecentReports([]);
            } finally {
                setReportsLoading(false);
            }
        }

        fetchData();
    }, []);

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return { primary: '#4caf50', secondary: '#e8f5e8', accent: '#2e7d32' };
            case 'processing':
                return { primary: '#ff9800', secondary: '#fff3e0', accent: '#f57c00' };
            case 'failed':
                return { primary: '#f44336', secondary: '#ffebee', accent: '#c62828' };
            default:
                return { primary: '#757575', secondary: '#f5f5f5', accent: '#616161' };
        }
    };

    return (
        <Box sx={styles.root(backgroundColor)}>
            {/* Hero Section */}
            <Box sx={styles.hero}>
                <Box sx={styles.heroInner}>
                    <Typography
                        variant="h4"
                        fontWeight="600"
                        gutterBottom
                        sx={styles.heroTitle}
                    >
                        <FormattedMessage id="home.welcome_message" />, {activeUser ? activeUser.first_name : "User"}
                    </Typography>
                </Box>
            </Box>

            {/* Recent Reports Section */}
            <Box sx={styles.recentReportsSection}>
                <Box sx={styles.recentReportsHeader}>
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        gutterBottom
                        color="#00438E"
                        sx={styles.recentReportsTitle}
                    >
                        <FormattedMessage id="home.recent_reports" defaultMessage="Reports" />
                    </Typography>
                    <Button
                        component={Link}
                        to="/insights"
                        variant="outlined"
                        size="small"
                        sx={styles.viewAllButton}
                    >
                        <FormattedMessage id="home.view_all_reports" defaultMessage="View All Reports" />
                    </Button>
                </Box>

                <Box sx={styles.reportsContainer}>
                    {reportsLoading ? (
                        <Box sx={styles.loadingBox}>
                            <CircularProgress sx={styles.loadingSpinner} />
                            <Typography variant="body1" sx={styles.loadingText}>
                                <FormattedMessage id="home.loading_reports" defaultMessage="Loading reports..." />
                            </Typography>
                        </Box>
                    ) : recentReports.length === 0 ? (
                        <Box sx={styles.emptyState}>
                            <Typography variant="h6" color="textSecondary" gutterBottom>
                                <FormattedMessage id="home.no_reports" defaultMessage="No reports available." />
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={styles.reportsScroll}>
                            {recentReports.map((report, index) => {
                                const statusColors = getStatusColor(report.status);

                                return (
                                    <Card
                                        key={report.id || index}
                                        sx={styles.reportCard(statusColors)}
                                    >
                                        <CardContent sx={styles.cardContent}>
                                            {/* Header Section */}
                                            <Box sx={styles.cardHeader}>
                                                <Box sx={styles.cardHeaderContent}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={styles.cardTitle}
                                                    >
                                                        {report.surveyName}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary" sx={styles.cardResponses}>
                                                        <FormattedMessage id="home.responses" values={{ count: report.totalResponses }} />
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={report.status?.toUpperCase()}
                                                    size="small"
                                                    sx={styles.reportCardChip(statusColors)}
                                                />
                                            </Box>

                                            {/* Metrics */}
                                            <Box sx={styles.cardMetrics}>
                                                <Box sx={styles.cardMetricsRow}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        <FormattedMessage id="home.created_at" defaultMessage="Created:" />
                                                    </Typography>
                                                    <Typography variant="body2" sx={styles.reportCardDate(statusColors)}>
                                                        {new Date(report.lastUpdated).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>

                                        {/* Action Buttons */}
                                        <CardActions sx={styles.reportCardActions(statusColors)}>
                                            <Button
                                                component={Link}
                                                to={`/insights/${report.id}`}
                                                size="small"
                                                variant="contained"
                                                sx={styles.viewReportButton(statusColors)}
                                            >
                                                <FormattedMessage id="home.view_report" defaultMessage="View Report" />
                                            </Button>
                                        </CardActions>
                                    </Card>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>

            <Box sx={styles.footer}>
                <Box sx={styles.footerInner}>
                    <Typography
                        variant="h6"
                        gutterBottom
                        sx={styles.footerTitle}
                    >
                        <FormattedMessage id="home.footer_title" />
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={styles.footerCopyright}
                    >
                        <FormattedMessage id="home.footer_copyright" values={{ year: 2026 }} />
                    </Typography>
                    <Box sx={styles.footerButtons}>
                        <Button
                            component={Link}
                            to="https://promptaai.com/en/2024-privacy-policy/"
                            color="inherit"
                            sx={styles.footerButton}
                        >
                            <FormattedMessage id="home.footer_privacy" />
                        </Button>
                        <Box sx={styles.footerDivider} />
                        <Button
                            component={Link}
                            to="https://promptaai.com/en/2024-terms-and-conditions/"
                            color="inherit"
                            sx={styles.footerButton}
                        >
                            <FormattedMessage id="home.footer_terms" />
                        </Button>
                        <Box sx={styles.footerDivider} />
                        <Button
                            component={Link}
                            to="https://promptaai.com/en/contact/"
                            color="inherit"
                            sx={styles.footerButton}
                        >
                            <FormattedMessage id="home.footer_contact" />
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box >
    );
};

const styles = {
    root: (backgroundColor) => ({
        minHeight: "100%",
        backgroundColor: backgroundColor || "#f5f5f5",
        pt: { xs: 2, sm: 3, md: 5 },
        px: { xs: 2, sm: 3 }
    }),
    hero: {
        background: "linear-gradient(135deg, #00438E 0%, #4A87C8 100%)",
        color: "white",
        borderRadius: 3,
        p: { xs: 4, sm: 4, md: 4 },
        py: 2,
        mb: 4,
        textAlign: "center",
        boxShadow: "0 8px 32px rgba(0, 67, 142, 0.15)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)",
            pointerEvents: "none"
        }
    },
    heroInner: {
        position: "relative",
        display: "flex",
        zIndex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
    },
    heroTitle: {
        fontFamily: "'Poppins', 'Inter', 'Roboto', sans-serif",
        fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
        mb: 1,
        opacity: 0.95,
        letterSpacing: "-0.01em",
        fontWeight: 600
    },
    recentReportsSection: {
        mb: 6
    },
    recentReportsHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2
    },
    recentReportsTitle: {
        mb: 0,
        fontFamily: "'Poppins', 'Inter', 'Roboto', sans-serif",
        letterSpacing: "-0.01em"
    },
    viewAllButton: {
        borderColor: "#00438E",
        color: "#00438E",
        "&:hover": {
            borderColor: "#003A75",
            backgroundColor: "rgba(0, 67, 142, 0.04)"
        }
    },
    reportsContainer: {
        backgroundColor: "#f8f9fa",
        border: "2px dashed rgba(0, 67, 142, 0.2)",
        borderRadius: "16px",
        p: 3,
        transition: "all 0.3s ease",
        "&:hover": {
            borderColor: "rgba(0, 67, 142, 0.3)",
            backgroundColor: "#f5f7fa"
        }
    },
    loadingBox: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        py: 8
    },
    loadingSpinner: {
        color: "#00438E"
    },
    loadingText: {
        ml: 2,
        color: "textSecondary"
    },
    emptyState: {
        textAlign: "center",
        py: 8
    },
    reportsScroll: {
        display: "flex",
        gap: 3,
        overflowX: "auto",
        pb: 1,
        px: 1,
        "&::-webkit-scrollbar": {
            height: "6px"
        },
        "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0, 67, 142, 0.08)",
            borderRadius: "3px"
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#00438E",
            borderRadius: "3px",
            "&:hover": {
                backgroundColor: "#003A75"
            }
        },
        scrollbarWidth: "thin",
        scrollbarColor: "#00438E rgba(0, 67, 142, 0.08)"
    },
    cardContent: {
        p: 3
    },
    cardHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        mb: 2
    },
    cardHeaderContent: {
        flex: 1,
        pr: 1
    },
    cardTitle: {
        fontWeight: 600,
        color: "#1a1a1a",
        lineHeight: 1.3,
        mb: 0.5,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden"
    },
    cardResponses: {
        fontSize: "0.8rem"
    },
    cardMetrics: {
        mb: 2
    },
    cardMetricsRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
    },
    reportCard: (statusColors) => ({
        minWidth: { xs: "300px", sm: "320px", md: "340px" },
        maxWidth: { xs: "300px", sm: "320px", md: "340px" },
        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
        border: `1px solid ${statusColors.secondary}`,
        borderLeft: `4px solid ${statusColors.primary}`,
        borderRadius: "12px",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        background: `linear-gradient(135deg, #ffffff 0%, ${statusColors.secondary}15 100%)`,
        "&:hover": {
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            borderColor: statusColors.primary,
            "& .MuiCardContent-root": {
                background: `linear-gradient(135deg, #ffffff 0%, ${statusColors.secondary}25 100%)`
            }
        },
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: `linear-gradient(90deg, ${statusColors.primary}, ${statusColors.accent})`
        }
    }),
    reportCardChip: (statusColors) => ({
        backgroundColor: statusColors.secondary,
        color: statusColors.primary,
        fontWeight: 600,
        fontSize: "0.7rem",
        height: "24px",
        border: `1px solid ${statusColors.primary}30`,
        "& .MuiChip-label": {
            px: 1
        }
    }),
    reportCardDate: (statusColors) => ({
        fontWeight: 600,
        color: statusColors.primary
    }),
    reportCardActions: (statusColors) => ({
        justifyContent: "flex-end",
        px: 3,
        py: 2,
        backgroundColor: "rgba(0, 0, 0, 0.02)",
        borderTop: `1px solid ${statusColors.secondary}50`
    }),
    viewReportButton: (statusColors) => ({
        backgroundColor: statusColors.primary,
        color: "white",
        textTransform: "none",
        fontSize: "0.75rem",
        fontWeight: 500,
        borderRadius: "6px",
        px: 2,
        py: 0.5,
        minWidth: "auto",
        boxShadow: "none",
        "&:hover": {
            backgroundColor: statusColors.accent,
            boxShadow: `0 2px 8px ${statusColors.primary}40`,
            transform: "translateY(-1px)"
        }
    }),
    footer: {
        background: "linear-gradient(135deg, #00438E 0%, #003A75 50%, #00355C 100%)",
        color: "white",
        p: { xs: 3, sm: 4, md: 5 },
        borderRadius: "16px",
        textAlign: "center",
        boxShadow: "0 8px 32px rgba(0, 67, 142, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)",
            pointerEvents: "none"
        }
    },
    footerInner: {
        position: "relative",
        zIndex: 1
    },
    footerTitle: {
        mb: 1.5,
        fontFamily: "'Poppins', 'Inter', 'Roboto', sans-serif",
        fontWeight: 600,
        fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
        letterSpacing: "0.02em"
    },
    footerCopyright: {
        opacity: 0.9,
        fontFamily: "'Inter', 'Roboto', sans-serif",
        fontSize: { xs: "0.875rem", sm: "0.9rem" },
        mb: 3
    },
    footerButtons: {
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 1.5, sm: 2 },
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap"
    },
    footerButton: {
        fontFamily: "'Inter', 'Roboto', sans-serif",
        fontSize: { xs: "0.875rem", sm: "0.9rem" },
        fontWeight: 500,
        textTransform: "none",
        px: { xs: 2, sm: 3 },
        py: { xs: 0.75, sm: 1 },
        borderRadius: "8px",
        transition: "all 0.3s ease",
        "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            transform: "translateY(-2px)"
        }
    },
    footerDivider: {
        display: { xs: "none", sm: "block" },
        width: "1px",
        height: "20px",
        backgroundColor: "rgba(255, 255, 255, 0.3)"
    }
}

export default HomePage;
